import { inject } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";
import { Observable, catchError, map, of } from "rxjs";

import { AuthService, isNetworkError } from "src/app/core";

import { Permissions } from "src/assets/config/config";

export interface CachedEligibility<T> {
  /** The eligibility request. Its cache policy lives on the service method it calls (T-10). */
  fetch: () => Observable<T>;
  /** Does this answer permit the route to open? */
  allow: (answer: T) => boolean;
  /**
   * Run when the server gave a real answer and that answer was "no" — a redirect, or a dialog
   * explaining the emptiness.
   *
   * Never called when the request failed. That is the whole point: a dialog reading "there are no
   * packages today" must only ever appear when the server actually said so.
   */
  onDenied?: () => void;
}

/**
 * @description
 * An eligibility check in `canActivate` that survives having no network.
 *
 * Three of the booking routes decide whether to open by asking the server — `Packages` asks whether
 * any packages exist, `TourismCompanies` and `FriendsOfOmanMuseum` ask whether the user may add the
 * service. Written the obvious way, each has only two states, and a failed request falls into the
 * "no" branch: offline, `Packages` told the clerk there were no packages today and the other two
 * silently redirected to `/services`. A cached app shell and a fully warmed read cache still left a
 * dead end at the first tap. This helper adds the missing third state.
 *
 * | Situation | Result |
 * | --- | --- |
 * | The server (or the cache) answered, and the answer permits it | Route opens |
 * | The server answered "no" | `onDenied()`, route blocked — unchanged |
 * | Could not ask, staff user | **Route opens.** The component renders the offline state (T-12) |
 * | Could not ask, everyone else | Toast, route blocked — unchanged |
 *
 * **Why the error, not `NetworkStatusService.isOffline()`.** `isNetworkError()` is the more precise
 * question: it is true exactly when this request could not be answered — which includes the case
 * where `navigator.onLine` is still optimistically `true` and the signal has not caught up yet. It
 * covers `OfflineUnavailableError` (offline, nothing usable cached) and a raw status-0 failure alike,
 * so it keeps working if a policy is ever removed from one of these endpoints.
 *
 * **Why staff only.** `servicesRoutes` is mounted twice — at `/services` for the public and at
 * `/admin/booking-services` for staff — so these guards run on both surfaces. Offline support is
 * scoped to staff (plan §5): their cache is the one the warm-up fills, and offline submit (T-17) is
 * theirs alone. Opening the route for a visitor on a flaky phone would hand them a form they cannot
 * complete. The gate lives here rather than in a forked route tree, as T-11 asks.
 *
 * @example
 * canActivate: [
 *   () => {
 *     const router = inject(Router);
 *     return cachedEligibilityGuard({
 *       fetch: () => inject(MembershipsService).checkAddingService(),
 *       allow: (status) => status === 1,
 *       onDenied: () => router.navigate(["/services"]),
 *     });
 *   },
 * ]
 */
export const cachedEligibilityGuard = <T>({ fetch, allow, onDenied }: CachedEligibility<T>): Observable<boolean> => {
  const auth = inject(AuthService);
  const toastr = inject(ToastrService);
  const translate = inject(TranslateService);

  const isStaff = Permissions.BookingServices.includes(auth.userRole()!);

  return fetch().pipe(
    map((answer) => {
      if (allow(answer)) return true;

      onDenied?.();
      return false;
    }),
    catchError((error: unknown) => {
      if (isNetworkError(error) && isStaff) return of(true);

      // Blocked, but never silently: without a toast the clerk taps a service card and nothing at
      // all happens. `NO_CONNECTION_SERVICE` rather than `NO_CONNECTION`, which is worded for a
      // submit that did not go through and would be nonsense here.
      toastr.error(translate.instant(isNetworkError(error) ? "ALERT.NO_CONNECTION_SERVICE" : "ALERT.SERVICE_UNAVAILABLE"));
      return of(false);
    }),
  );
};
